import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Link2, Loader2, X } from 'lucide-react'
import { useDocumentList } from '../../hooks/useDocuments'
import { useRelateDocument } from '../../hooks/useDocumentTree'

interface LinkDocumentPanelProps {
  koblinkId: string
  actorId?: string
  onClose: () => void
}

const RELATION_TYPES = ['amendment', 'renewal', 'related'] as const

export function LinkDocumentPanel({ koblinkId, actorId, onClose }: LinkDocumentPanelProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [relationType, setRelationType] = useState<typeof RELATION_TYPES[number]>('related')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data } = useDocumentList({
    owner: actorId,
    filters: {},
  })
  const relateMutation = useRelateDocument()

  // Filter by search and exclude current document
  const docs = (data?.results ?? []).filter(
    (d) =>
      d.koblink_id !== koblinkId &&
      (!search || d.title.toLowerCase().includes(search.toLowerCase())),
  )

  const handleLink = () => {
    if (!selectedId) return
    setError(null)
    relateMutation.mutate(
      { koblinkId, parentKoblinkId: selectedId, relationType, actorId },
      {
        onSuccess: () => onClose(),
        onError: (err) => setError(err instanceof Error ? err.message : t('common.error')),
      },
    )
  }

  return (
    <div className="mt-2 p-2 rounded border border-neutral-200 dark:border-neutral-700 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium text-neutral-700 dark:text-neutral-300">
          {t('related.linkDocument')}
        </span>
        <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
          <X size={12} />
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={10} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('related.searchDocuments')}
          className="w-full text-[11px] pl-6 pr-2 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 placeholder-neutral-400"
        />
      </div>

      {/* Document list */}
      <div className="max-h-32 overflow-y-auto space-y-0.5">
        {docs.length === 0 ? (
          <p className="text-[10px] text-neutral-400 text-center py-2">
            {t('status.noDocuments')}
          </p>
        ) : (
          docs.map((doc) => (
            <button
              key={doc.koblink_id}
              onClick={() => setSelectedId(doc.koblink_id)}
              className={`w-full text-left px-2 py-1 rounded text-[11px] transition-colors ${
                selectedId === doc.koblink_id
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800'
                  : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50 text-neutral-700 dark:text-neutral-300'
              }`}
            >
              <span className="truncate block">{doc.title}</span>
              <span className="text-[9px] text-neutral-400">
                {t(`status.signingStatus.${doc.status}`)}
              </span>
            </button>
          ))
        )}
      </div>

      {/* Relation type */}
      <select
        value={relationType}
        onChange={(e) => setRelationType(e.target.value as typeof RELATION_TYPES[number])}
        className="w-full text-[11px] px-2 py-1 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
      >
        {RELATION_TYPES.map((type) => (
          <option key={type} value={type}>
            {t(`related.relationType.${type}`)}
          </option>
        ))}
      </select>

      {error && <p className="text-[10px] text-error-500">{error}</p>}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={handleLink}
          disabled={!selectedId || relateMutation.isPending}
          className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] py-1 rounded bg-primary-500 hover:bg-primary-600 text-white disabled:opacity-50 transition-colors"
        >
          {relateMutation.isPending ? <Loader2 size={10} className="animate-spin" /> : <Link2 size={10} />}
          {t('related.linkDocument')}
        </button>
        <button
          onClick={onClose}
          className="flex-1 text-[11px] py-1 rounded border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  )
}
